import { Listbox, ListboxButton, ListboxOption, ListboxOptions } from "@headlessui/react";
import { CheckIcon, ChevronUpDownIcon } from "@heroicons/react/24/outline";
import clsx from "clsx";
import { useMemo } from "react";

import { Badge } from "@/components/Badge";

type AcademicYearOption = {
  value: string;
  label: string;
  isActive?: boolean;
};

type Props = {
  options: AcademicYearOption[];
  value: string;
  onChange: (academicYearId: string) => void;
  disabled?: boolean;
  label?: string;
  placeholder?: string;
  className?: string;
  title?: string;
  error?: string;
  helperText?: string;
  reserveMessageSpace?: boolean;
  labelInline?: boolean;
};

export function AcademicYearSelect({
  options,
  value,
  onChange,
  disabled = false,
  label = "Academic Year",
  placeholder = "Select academic year",
  className,
  title,
  error,
  helperText,
  reserveMessageSpace = false,
  labelInline = false,
}: Props) {
  const selectedOption = useMemo(
    () => options.find((option) => option.value === value) ?? null,
    [options, value],
  );

  if (labelInline) {
    return (
      <div className="flex items-center gap-2">
        {label ? (
          <span className="shrink-0 text-xs font-medium text-slate-500 dark:text-slate-400">
            {label}
          </span>
        ) : null}
        <Listbox value={value} onChange={onChange} disabled={disabled || !options.length}>
          <div className="relative">
            <ListboxButton
              className={clsx(
                "relative flex items-center justify-between rounded-md border border-slate-200 bg-white px-3 py-1.5 text-left text-sm text-slate-900 shadow-sm focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-100 disabled:cursor-not-allowed disabled:bg-slate-50 disabled:text-slate-500 dark:border-slate-700 dark:bg-slate-900 dark:text-white dark:focus:border-blue-400 dark:focus:ring-blue-500/10",
                error && "border-red-500 focus:border-red-500 focus:ring-red-500",
                className,
              )}
              title={title}
            >
              <span className="flex min-w-0 items-center gap-2 pr-5">
                <span className="truncate">{selectedOption?.label ?? placeholder}</span>
                {selectedOption?.isActive ? (
                  <Badge variant="success" size="sm">
                    Active
                  </Badge>
                ) : null}
              </span>
              <ChevronUpDownIcon className="h-4 w-4 text-slate-400" />
            </ListboxButton>

            <ListboxOptions className="absolute right-0 z-50 mt-1 max-h-64 w-max min-w-full overflow-auto rounded-md border border-slate-200 bg-white p-1 shadow-lg focus:outline-none dark:border-slate-700 dark:bg-slate-900">
              {options.map((option) => (
                <ListboxOption
                  key={option.value}
                  value={option.value}
                  className={({ focus }) =>
                    clsx(
                      "cursor-pointer rounded-md px-2 py-2",
                      focus
                        ? "bg-blue-50 text-blue-900 dark:bg-blue-500/20 dark:text-blue-100"
                        : "text-slate-900 dark:text-slate-100",
                    )
                  }
                >
                  {({ selected }) => (
                    <div className="flex items-center justify-between gap-2">
                      <div className="flex min-w-0 items-center gap-2">
                        <span className="truncate">{option.label}</span>
                        {option.isActive ? (
                          <Badge variant="success" size="sm">
                            Active
                          </Badge>
                        ) : null}
                      </div>
                      {selected ? <CheckIcon className="h-4 w-4 shrink-0" /> : null}
                    </div>
                  )}
                </ListboxOption>
              ))}
            </ListboxOptions>
          </div>
        </Listbox>
      </div>
    );
  }

  return (
    <div className="space-y-1">
      {label ? <label className="form-label block">{label}</label> : null}

      <Listbox value={value} onChange={onChange} disabled={disabled || !options.length}>
        <div className="relative">
          <ListboxButton
            className={clsx(
              "relative flex w-full items-center justify-between rounded-md border border-slate-200 bg-white px-3 py-2.5 text-left text-sm text-slate-900 shadow-sm focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-100 disabled:cursor-not-allowed disabled:bg-slate-50 disabled:text-slate-500 dark:border-slate-700 dark:bg-slate-900 dark:text-white dark:focus:border-blue-400 dark:focus:ring-blue-500/10",
              error && "border-red-500 focus:border-red-500 focus:ring-red-500",
              className,
            )}
            title={title}
          >
            <span className="flex min-w-0 items-center gap-2 pr-6">
              <span className="truncate">{selectedOption?.label ?? placeholder}</span>
              {selectedOption?.isActive ? (
                <Badge variant="success" size="sm">
                  Active
                </Badge>
              ) : null}
            </span>
            <ChevronUpDownIcon className="h-4 w-4 text-slate-400" />
          </ListboxButton>

          <ListboxOptions className="absolute z-50 mt-1 max-h-64 w-full overflow-auto rounded-md border border-slate-200 bg-white p-1 shadow-lg focus:outline-none dark:border-slate-700 dark:bg-slate-900">
            {options.map((option) => (
              <ListboxOption
                key={option.value}
                value={option.value}
                className={({ focus }) =>
                  clsx(
                    "cursor-pointer rounded-md px-2 py-2",
                    focus
                      ? "bg-blue-50 text-blue-900 dark:bg-blue-500/20 dark:text-blue-100"
                      : "text-slate-900 dark:text-slate-100",
                  )
                }
              >
                {({ selected }) => (
                  <div className="flex items-center justify-between gap-2">
                    <div className="flex min-w-0 items-center gap-2">
                      <span className="truncate">{option.label}</span>
                      {option.isActive ? (
                        <Badge variant="success" size="sm">
                          Active
                        </Badge>
                      ) : null}
                    </div>
                    {selected ? <CheckIcon className="h-4 w-4 shrink-0" /> : null}
                  </div>
                )}
              </ListboxOption>
            ))}
          </ListboxOptions>
        </div>
      </Listbox>

      {(error || helperText || reserveMessageSpace) && (
        <p
          className={clsx(
            "min-h-[1.25rem] text-sm",
            error
              ? "text-red-600 dark:text-red-400"
              : helperText
                ? "text-gray-500 dark:text-gray-400"
                : "text-transparent",
          )}
        >
          {error ?? helperText ?? " "}
        </p>
      )}
    </div>
  );
}
